import { Vector2 } from 'three';
import {
  Areas,
  Design,
  LassieMini,
  Lassie,
  LoadType,
  ShuttleRack,
  VehicleType,
  HikStorage,
} from '@thive/fddm-types';
import { t } from 'i18next';

import { templateRequestFactory } from '@/modules/floorplanDesign/factories';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { CanvasServiceFactory } from '@/modules/canvas/services/Canvas/CanvasServiceFactory';
import { SnackbarServiceFactory } from '@/modules/snackbar/service/SnackbarServiceFactory';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { IFddmService } from '@/modules/canvas/services/Floorplan/IFddmService';
import { ICanvasService } from '@/modules/canvas/services/Canvas/ICanvasService';
import { resolveFirstAvailable } from '@/modules/common/helpers/array';
import {
  getValidShuttleLoadCarriers,
  HIK_PICKING_VEHICLE_NAMES,
  HIK_TRANSPORT_VEHICLE_NAMES,
  LASSIE_PICKING_VEHICLE_NAMES,
  LASSIE_TRANSPORT_VEHICLE_NAMES,
  LOAD_CARRIERS_CARRIABLE_BY_SHUTTLES_IDS,
  SHUTTLE_LOAD_CARRIERS_IDS,
  VEHICLES_ABLE_TO_CARRY_SHUTTLES_NAMES,
} from '@/modules/vehicles/constants';
import { HIK_LOAD_CARRIER_IDS, LASSIE_LOAD_CARRIER_IDS } from '@/modules/loadCarriers/constants';
import { ISnackbarService } from '@/modules/snackbar/service/ISnackbarService';
import { IAppService } from '@/modules/app/service/IAppService';

import { ITpsService } from './ITpsService';
import { templatesToFlatCollection } from '@/modules/floorplanDesign/helpers/helpers';
import { TemplateApiConnector } from '@/modules/api/TemplateApiConnector/TemplateApiConnector';
import { ITemplateApiConnector } from '@/modules/api/TemplateApiConnector/ITemplateApiConnector';
import { logger } from '@common/logging/logger';

export class TpsServiceImpl implements ITpsService {
  private fddmStore: typeof useFloorplanDesignStore;
  private fddmSvc: IFddmService;
  private snackbarSvc: ISnackbarService;
  private canvasSvc: ICanvasService;
  private tpsConnector: ITemplateApiConnector;
  private appSvc: IAppService;

  public constructor(fddmStore: typeof useFloorplanDesignStore) {
    this.fddmStore = fddmStore;
    this.fddmSvc = FddmServiceFactory.getInstance().createFddmService();
    this.snackbarSvc = SnackbarServiceFactory.getInstance().createSnackbarService();
    this.canvasSvc = CanvasServiceFactory.getInstance().createCanvasService();
    this.tpsConnector = TemplateApiConnector.getInstance();
    this.appSvc = AppServiceFactory.getInstance().createAppService();
  }

  public async generateLassieTemplate(center: Vector2): Promise<void> {
    try {
      const lassieAssets = this.getLassieAssets();
      if (!lassieAssets) {
        return;
      }

      const result = await this.tpsConnector.generateLassie(
        templateRequestFactory.buildInitialTemplateRequest({
          templateType: 'LASSIE',
          creationParameters: {
            mousePos: center,
            toteLoadType: lassieAssets.loadType,
            transportVehicleUvtFileRef: lassieAssets.transportVehicle?.uvtFileRef,
            pickingVehicleUvtFileRef: lassieAssets.pickingVehicle?.uvtFileRef,
          },
        }),
      );

      if (!result || !result.design) {
        logger.error('Failed to generate template:', result?.notifications);
        return;
      }

      await this.addTemplate(result.design);
      this.canvasSvc.setSelection([result.design.templates.lassies[0].group.id]);
    } catch (err) {
      logger.error('Failed to generate template. Error: ', err);
    }
  }

  public async regenerateLassieTemplate(
    lassieGroup: Lassie,
  ): Promise<{ result: 'success'; data: Design } | { result: 'failed' }> {
    try {
      const previousAreas = this.getTemplateAreas(lassieGroup.group.areaIds);
      const lassieAssets = this.getLassieAssets();
      if (!lassieAssets) {
        throw new Error('Could not determine lassie assets');
      }

      const flow = this.fddmStore
        .getState()
        .design.flows.flows.find((f) => f.type === 'GOODS_TO_PERSONS');

      if (!flow || !flow.goodsToPersonsExtension) {
        throw new Error('Failed to regenerate template: Goods to Persons flow not found in state');
      }
      const result = await this.tpsConnector.generateLassie(
        templateRequestFactory.buildTemplateRequest({
          templateType: 'LASSIE',
          creationParameters: {
            params: lassieGroup.params,
            groupName: lassieGroup.group.name,
            toteLoadType: lassieAssets.loadType,
            transportUvtFileRef: lassieAssets.transportVehicle?.uvtFileRef,
            pickingUvtFileRef: lassieAssets.pickingVehicle?.uvtFileRef,
            hourlyOrderCounts: flow.hourlyOrderCounts,
            flow: flow.goodsToPersonsExtension,
          },
        }),
      );

      if (!result || !result.design) {
        throw new Error('Failed to generate template: ', {
          cause: result?.notifications.at(0),
        });
      }

      const { design } = result;

      await this.updateTemplate(design, previousAreas);

      return { result: 'success', data: design };
    } catch (err) {
      logger.error('Failed to regenerate template. Error: ', err);
    }

    return { result: 'failed' };
  }

  public async generateLassieMiniTemplate(center: Vector2): Promise<void> {
    try {
      const lassieAssets = this.getLassieAssets();
      if (!lassieAssets) {
        throw new Error('Could not determine lassie assets');
      }

      const result = await this.tpsConnector.generateLassieMini(
        templateRequestFactory.buildInitialTemplateRequest({
          templateType: 'LASSIE_MINI',
          creationParameters: {
            mousePos: center,
            toteLoadType: lassieAssets.loadType,
            transportVehicleUvtFileRef: lassieAssets.transportVehicle?.uvtFileRef,
            pickingVehicleUvtFileRef: lassieAssets.pickingVehicle?.uvtFileRef,
          },
        }),
      );

      if (!result || !result.design) {
        logger.error('Failed to generate template:', result?.notifications);
        return;
      }

      await this.addTemplate(result.design);

      this.canvasSvc.setSelection([result.design.templates.lassieMinis[0].group.id]);
    } catch (err) {
      logger.error('Failed to generate template. Error: ', err);
    }
  }

  public async regenerateLassieMiniTemplate(
    lassieMiniGroup: LassieMini,
  ): Promise<{ result: 'success'; data: Design } | { result: 'failed' }> {
    try {
      const previousAreas = this.getTemplateAreas(lassieMiniGroup.group.areaIds);
      const lassieAssets = this.getLassieAssets();
      if (!lassieAssets) {
        throw new Error('Could not determine lassie assets');
      }

      const result = await this.tpsConnector.generateLassieMini(
        templateRequestFactory.buildTemplateRequest({
          templateType: 'LASSIE_MINI',
          creationParameters: {
            params: lassieMiniGroup.params,
            groupName: lassieMiniGroup.group.name,
            toteLoadType: lassieAssets.loadType,
            transportUvtFileRef: lassieAssets.transportVehicle?.uvtFileRef,
            pickingUvtFileRef: lassieAssets.pickingVehicle?.uvtFileRef,
          },
        }),
      );

      if (!result || !result.design) {
        throw new Error('Failed to generate template: ', {
          cause: result?.notifications.at(0),
        });
      }

      const { design } = result;

      await this.updateTemplate(design, previousAreas);

      return { result: 'success', data: design };
    } catch (err) {
      logger.error('Failed to regenerate template. Error: ', err);
    }

    return { result: 'failed' };
  }

  public async generateShuttleTemplate(center: Vector2): Promise<void> {
    try {
      const shuttleAssets = this.getShuttleAssets();
      if (!shuttleAssets) {
        return;
      }

      const result = await this.tpsConnector.generateShuttle(
        templateRequestFactory.buildInitialTemplateRequest({
          templateType: 'SHUTTLE',
          creationParameters: {
            mousePos: center,
            carrierLoad: shuttleAssets.carrierLoadType,
            shuttleLoad: shuttleAssets.shuttleLoadType,
            shuttleUvtFileRef: shuttleAssets.shuttleVehicle?.uvtFileRef,
            forkLiftUvtFileRef: shuttleAssets.shuttleCarryingVehicle?.uvtFileRef,
          },
        }),
      );

      if (!result.design) {
        logger.error('Failed to generate template:', result.notifications);
        return;
      }

      await this.addTemplate(result.design);
      this.canvasSvc.setSelection([result.design.templates.shuttleRacks[0].group.id]);
    } catch (err) {
      logger.error('Failed to generate template. Error: ', err);
    }
  }

  public async regenerateShuttleTemplate(
    shuttleGroup: ShuttleRack,
  ): Promise<{ result: 'success'; data: Design } | { result: 'failed' }> {
    try {
      const previousAreas = this.getTemplateAreas(shuttleGroup.group.areaIds);
      const shuttleAssets = this.getShuttleAssets();
      if (!shuttleAssets) {
        throw new Error('Could not determine shuttle assets');
      }

      const result = await this.tpsConnector.generateShuttle(
        templateRequestFactory.buildTemplateRequest({
          templateType: 'SHUTTLE',
          creationParameters: {
            params: shuttleGroup.params,
            groupName: shuttleGroup.group.name,
            carrierLoadType: shuttleAssets.carrierLoadType,
            shuttleLoadType: shuttleAssets.shuttleLoadType,
            shuttleUvtFileRef: shuttleAssets.shuttleVehicle?.uvtFileRef,
            forkLiftUvtFileRef: shuttleAssets.shuttleCarryingVehicle?.uvtFileRef,
          },
        }),
      );

      if (!result || !result.design) {
        throw new Error('Failed to generate template: ', {
          cause: result?.notifications.at(0),
        });
      }

      const { design } = result;

      await this.updateTemplate(design, previousAreas);

      return { result: 'success', data: design };
    } catch (err) {
      logger.error('Failed to regenerate template. Error: ', err);
    }

    return { result: 'failed' };
  }

  public async generateHikTemplate(center: Vector2): Promise<void> {
    try {
      const hikAssets = this.getHikAssets();
      if (!hikAssets) {
        return;
      }

      const result = await this.tpsConnector.generateHik(
        templateRequestFactory.buildInitialTemplateRequest({
          templateType: 'HIK',
          creationParameters: {
            mousePos: center,
            toteLoadType: hikAssets.loadType,
            transportVehicleUvtFileRef: hikAssets.transportVehicle?.uvtFileRef,
            pickingVehicleUvtFileRef: hikAssets.pickingVehicle?.uvtFileRef,
          },
        }),
      );

      if (!result || !result.design) {
        logger.error('Failed to generate template:', result?.notifications);
        return;
      }

      await this.addTemplate(result.design);
      const hikStorageGroupId = result.design.templates.hikStorages?.[0]?.group.id;
      if (hikStorageGroupId) {
        this.canvasSvc.setSelection([hikStorageGroupId]);
      }
    } catch (err) {
      logger.error('Failed to generate template. Error: ', err);
    }
  }

  public async regenerateHikTemplate(
    hikGroup: HikStorage,
  ): Promise<{ result: 'success'; data: Design } | { result: 'failed' }> {
    try {
      const previousAreas = this.getTemplateAreas(hikGroup.group.areaIds);
      const hikAssets = this.getHikAssets();
      if (!hikAssets) {
        throw new Error('Could not determine hik assets');
      }

      const result = await this.tpsConnector.generateHik(
        templateRequestFactory.buildTemplateRequest({
          templateType: 'HIK',
          creationParameters: {
            params: hikGroup.params,
            groupName: hikGroup.group.name,
            toteLoadType: hikAssets.loadType,
            transportUvtFileRef: hikAssets.transportVehicle?.uvtFileRef,
            pickingUvtFileRef: hikAssets.pickingVehicle?.uvtFileRef,
          },
        }),
      );

      if (!result || !result.design) {
        throw new Error('Failed to generate template: ', {
          cause: result?.notifications.at(0),
        });
      }

      const { design } = result;

      await this.updateTemplate(design, previousAreas);

      return { result: 'success', data: design };
    } catch (err) {
      logger.error('Failed to regenerate template. Error: ', err);
    }

    return { result: 'failed' };
  }

  //
  // Private methods
  //

  private getShuttleAssets():
    | {
        carrierLoadType: LoadType;
        shuttleLoadType: LoadType;
        shuttleVehicle: VehicleType;
        shuttleCarryingVehicle: VehicleType;
      }
    | undefined {
    const { getLoadTypeById, getVehicleTypeByIdSilently, getLoadTypes, getVehicleTypes } =
      this.fddmStore.getState();

    const availableLoadTypeIds = new Set(getLoadTypes().map((loadType) => loadType.id));
    const availableVehicleTypeIds = new Set(getVehicleTypes().map((vehicleType) => vehicleType.id));

    const carrierLoadType = resolveFirstAvailable(
      LOAD_CARRIERS_CARRIABLE_BY_SHUTTLES_IDS,
      availableLoadTypeIds,
      getLoadTypeById,
    );
    const shuttleLoadType = resolveFirstAvailable(
      SHUTTLE_LOAD_CARRIERS_IDS,
      availableLoadTypeIds,
      getLoadTypeById,
    );
    const shuttleVehicle = resolveFirstAvailable(
      SHUTTLE_LOAD_CARRIERS_IDS,
      availableVehicleTypeIds,
      getVehicleTypeByIdSilently,
    );
    const shuttleCarryingVehicle = resolveFirstAvailable(
      VEHICLES_ABLE_TO_CARRY_SHUTTLES_NAMES,
      availableVehicleTypeIds,
      getVehicleTypeByIdSilently,
    );

    if (!carrierLoadType || !shuttleLoadType || !shuttleVehicle || !shuttleCarryingVehicle) {
      this.snackbarSvc.showMessage(t('errors:template.required_vehicles_not_selected.shuttle'));
      return;
    }

    const validShuttleLoadCarrier = getValidShuttleLoadCarriers(
      shuttleVehicle.id as 'RS150_800' | 'RS150_1000',
    );
    if (shuttleLoadType.id !== validShuttleLoadCarrier) {
      this.snackbarSvc.showMessage(
        t('errors:template.shuttle_load_carrier_and_vehicle_mismatch', {
          shuttleVehicle: shuttleVehicle.id,
          loadCarrier: shuttleLoadType.id,
        }),
      );
      return;
    }

    return {
      carrierLoadType,
      shuttleLoadType,
      shuttleVehicle,
      shuttleCarryingVehicle,
    };
  }

  private getLassieAssets():
    | {
        loadType: LoadType;
        transportVehicle: VehicleType;
        pickingVehicle: VehicleType;
      }
    | undefined {
    const { getLoadTypeById, getVehicleTypeByIdSilently, getLoadTypes, getVehicleTypes } =
      this.fddmStore.getState();

    const availableLoadTypeIds = new Set(getLoadTypes().map((loadType) => loadType.id));
    const availableVehicleTypeIds = new Set(getVehicleTypes().map((vehicleType) => vehicleType.id));

    const loadType = resolveFirstAvailable(
      LASSIE_LOAD_CARRIER_IDS,
      availableLoadTypeIds,
      getLoadTypeById,
    );
    const transportVehicle = resolveFirstAvailable(
      LASSIE_TRANSPORT_VEHICLE_NAMES,
      availableVehicleTypeIds,
      getVehicleTypeByIdSilently,
    );
    const pickingVehicle = resolveFirstAvailable(
      LASSIE_PICKING_VEHICLE_NAMES,
      availableVehicleTypeIds,
      getVehicleTypeByIdSilently,
    );

    if (!loadType || !transportVehicle || !pickingVehicle) {
      this.snackbarSvc.showMessage(t('errors:template.required_vehicles_not_selected.lassie'));
      return;
    }

    return {
      loadType,
      transportVehicle,
      pickingVehicle,
    };
  }

  private getHikAssets():
    | {
        loadType: LoadType;
        transportVehicle: VehicleType;
        pickingVehicle: VehicleType;
      }
    | undefined {
    const { getLoadTypeById, getVehicleTypeByIdSilently, getLoadTypes, getVehicleTypes } =
      this.fddmStore.getState();

    const availableLoadTypeIds = new Set(getLoadTypes().map((loadType) => loadType.id));
    const availableVehicleTypeIds = new Set(getVehicleTypes().map((vehicleType) => vehicleType.id));

    const loadType = resolveFirstAvailable(
      HIK_LOAD_CARRIER_IDS,
      availableLoadTypeIds,
      getLoadTypeById,
    );
    const transportVehicle = resolveFirstAvailable(
      HIK_TRANSPORT_VEHICLE_NAMES,
      availableVehicleTypeIds,
      getVehicleTypeByIdSilently,
    );
    const pickingVehicle = resolveFirstAvailable(
      HIK_PICKING_VEHICLE_NAMES,
      availableVehicleTypeIds,
      getVehicleTypeByIdSilently,
    );

    if (!loadType || !transportVehicle || !pickingVehicle) {
      this.snackbarSvc.showMessage(t('errors:template.required_vehicles_not_selected.hik'));
      return;
    }

    return {
      loadType,
      transportVehicle,
      pickingVehicle,
    };
  }

  private async addTemplate(design: Design): Promise<void> {
    const { areas, interAreaConnections, templates, flows } = design;
    const template = templatesToFlatCollection(templates)[0];

    await this.fddmSvc.addTemplate(template, areas, interAreaConnections, flows);

    this.appSvc.resetTool();
    this.canvasSvc.zoomToFit();
    this.selectTemplate(template.group.id);
  }

  private selectTemplate(id: string): void {
    this.canvasSvc.setSelection([id]);
    this.appSvc.setSelectedIds([id]);
  }

  private getTemplateAreas(areaIds: string[]): Areas {
    return this.fddmStore.getState().getAreasByIds(areaIds);
  }

  private async updateTemplate(design: Design, previousAreas: Areas): Promise<void> {
    try {
      this.appSvc.showLoadContainer('TRANSPARENT');

      const { areas, interAreaConnections, templates, flows } = design;
      const template = templatesToFlatCollection(templates)[0];
      await this.fddmSvc.regenerateTemplate(
        template,
        areas,
        interAreaConnections,
        flows,
        previousAreas,
      );
      this.selectTemplate(template.group.id);
    } finally {
      this.appSvc.hideLoadContainer();
    }
  }
}
